import React from 'react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-black text-white py-12 px-6 sm:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <h1 
          className="text-4xl md:text-6xl font-bold text-center mb-12 
          bg-gradient-to-r from-[#4da3ff] to-pink-500 
          bg-clip-text text-transparent"
        >
          SourceCodey Digital Marketplace Terms of Service
        </h1>

        <div className="space-y-10">

          {/* 1. Acceptance of Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              By accessing or using <span className="text-pink-500 font-semibold">SourceCodey</span> 
              ("Service"), the Number One Digital Products Marketplace, you agree to be bound 
              by these Terms of Service ("Terms"). If you do not agree, you must not access or use the Service. 
              SourceCodey reserves the right to modify these Terms at any time, with changes effective upon posting.
            </p>
          </section>

          {/* 2. Eligibility */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              2. Eligibility
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              You must be at least 13 years old to use this Service. By using SourceCodey, 
              you confirm that you meet this requirement and have the legal capacity to 
              enter into binding agreements. If you are under 18, parental or guardian 
              consent is required.
            </p>
          </section>

          {/* 3. Account Registration */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              3. Account Registration
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              To buy or sell digital products on SourceCodey, you must create an account. 
              You agree to provide accurate and complete information and to keep your 
              login credentials secure. You are fully responsible for all activity under your account.
            </p>
          </section>

          {/* 4. Use of the Marketplace */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              4. Use of the Marketplace
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              SourceCodey provides a platform for buying and selling digital products 
              including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-lg text-gray-300 space-y-2">
              <li>Software applications</li>
              <li>Source code & scripts</li>
              <li>UI/UX templates</li>
              <li>AI tools and digital assets</li>
              <li>Plugins and digital resources</li>
            </ul>
            <p className="text-lg text-gray-300 mt-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-lg text-gray-300 space-y-2">
              <li>List fraudulent or unauthorized digital products</li>
              <li>Upload malicious code or harmful files</li>
              <li>Infringe on intellectual property rights</li>
              <li>Attempt unauthorized access to platform systems</li>
            </ul>
          </section>

          {/* 5. Digital Product Ownership & Licensing */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              5. Digital Product Ownership & Licensing
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              Sellers retain ownership of their uploaded digital products. 
              By listing content on SourceCodey, you grant the platform a non-exclusive, 
              worldwide license to display and distribute your product for marketplace purposes.
              Buyers receive usage rights according to the license provided with each product.
            </p>
          </section>

          {/* 6. Payments, Fees & Refund Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              6. Payments, Fees & Refund Policy
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              Payments for digital products are processed via secure payment providers. Certain transactions may include platform service fees. 
              By purchasing or selling, you agree to all applicable charges displayed at checkout.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              <strong>Refund Policy:</strong> All digital product purchases are generally non-refundable due to instant delivery. 
              Refunds are only issued if required by law or in exceptional circumstances. If you encounter issues, please contact our support.
            </p>
          </section>

          {/* 7. Privacy Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              7. Privacy Policy
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              We value your privacy. Any personal information collected through SourceCodey 
              is used solely for providing and improving the Service, processing payments, 
              and communicating with users. We do not sell personal data to third parties. 
              By using the Service, you consent to our data practices.
            </p>
          </section>

          {/* 8. Prohibited Conduct */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              8. Prohibited Conduct
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              You may not:
            </p>
            <ul className="list-disc pl-6 text-lg text-gray-300 space-y-2">
              <li>Sell pirated or stolen digital products</li>
              <li>Impersonate another individual or brand</li>
              <li>Engage in deceptive or fraudulent transactions</li>
              <li>Use the platform for unlawful purposes</li>
            </ul>
          </section>

          {/* 9. Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              9. Termination
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              SourceCodey may suspend or terminate accounts that violate 
              these Terms without prior notice. Upon termination, your 
              access to the Service will immediately cease.
            </p>
          </section>

          {/* 10. Disclaimers & Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              10. Disclaimers & Limitation of Liability
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              The Service is provided "as is" without warranties of any kind. 
              SourceCodey does not guarantee uninterrupted access or error-free performance.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              To the maximum extent permitted by law, SourceCodey shall not be 
              liable for indirect or consequential damages arising from use of the Service. 
              Total liability shall not exceed the amount paid within the last 12 months.
            </p>
          </section>

          {/* 11. Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              11. Governing Law
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              These Terms shall be governed by the laws of Uganda. 
              Any disputes shall be resolved through arbitration in Kampala, Uganda.
            </p>
          </section>

          {/* 12. Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-[#4da3ff] mb-4">
              12. Contact Information
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              For questions regarding these Terms, the Refund Policy, or Privacy Policy, contact:
              <br />
              Email: support@sourcecodey.com
              <br />
              Address: SourceCodey Digital Marketplace, Kampala, Uganda
            </p>
          </section>

        </div>

        <p className="text-center text-gray-500 mt-16">
          Last Updated: March 2026
        </p>

      </div>
    </div>
  );
};

export default TermsOfService;